import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import type { Organization, UserWithMemberships } from "@scheduler/shared";
import { ApiException, api } from "./api";
import { supabase } from "./supabase";

type Status = "loading" | "anonymous" | "authed";

type AuthContextValue = {
  status: Status;
  user: UserWithMemberships | null;
  pending: boolean;
  isManager: boolean;
  /**
   * The user's currently-active organization. Populated from the /me
   * response so the SPA shell can render org-aware chrome without an
   * extra round-trip. NULL for super-admins and pending admins.
   */
  activeOrg: Organization | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: UserWithMemberships) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<UserWithMemberships | null>(null);
  const [pending, setPending] = useState(false);
  const [activeOrg, setActiveOrg] = useState<Organization | null>(null);

  const loadFromApi = useCallback(async () => {
    try {
      const res = await api.me();
      setUser(res.user);
      setPending(res.pending);
      setActiveOrg(res.active_org ?? null);
      setStatus("authed");
    } catch (err) {
      if (err instanceof ApiException && err.status === 401) {
        setUser(null);
        setPending(false);
        setActiveOrg(null);
        setStatus("anonymous");
      } else {
        setUser(null);
        setPending(false);
        setActiveOrg(null);
        setStatus("anonymous");
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap(session: Session | null) {
      if (cancelled) return;
      if (!session) {
        setUser(null);
        setPending(false);
        setActiveOrg(null);
        setStatus("anonymous");
        return;
      }
      await loadFromApi();
    }

    void supabase.auth.getSession().then(({ data }) => bootstrap(data.session));

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        void bootstrap(session);
      },
    );

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [loadFromApi]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setPending(false);
    setActiveOrg(null);
    setStatus("anonymous");
  }, []);

  const isManager =
    user !== null && (user.is_admin || user.managed_sub_department_ids.length > 0);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      pending,
      isManager,
      activeOrg,
      refresh: loadFromApi,
      signOut,
      setUser,
    }),
    [status, user, pending, isManager, activeOrg, loadFromApi, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth used outside of AuthProvider");
  return ctx;
}
